/**
 * URL 相關的工具函數
 */

/**
 * 獲取URL參數值
 * @param {string} name - 參數名稱
 * @param {string} defaultValue - 預設值
 * @param {string} url - 指定的URL（預設為當前頁面）
 * @returns {string|null} 參數值
 */
export const getUrlParam = (name, defaultValue = null, url = window.location.search) => {
  const urlParams = new URLSearchParams(url);
  return urlParams.get(name) || defaultValue;
};

/**
 * 設置URL參數（不刷新頁面）
 * @param {string} name - 參數名稱
 * @param {string} value - 參數值
 * @param {boolean} replace - 是否替換當前歷史記錄
 */
export const setUrlParam = (name, value, replace = true) => {
  const url = new URL(window.location);
  url.searchParams.set(name, value);
  
  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
};

/**
 * 移除URL參數（不刷新頁面）
 * @param {string} name - 參數名稱
 * @param {boolean} replace - 是否替換當前歷史記錄
 */
export const removeUrlParam = (name, replace = true) => {
  const url = new URL(window.location);
  url.searchParams.delete(name);
  
  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
};

/**
 * 檢查URL中的布林標記
 * @param {string} flagName - 標記名稱
 * @param {string} trueValue - 表示true的值（預設為'true'）
 * @returns {boolean} 是否為true
 */
export const checkUrlFlag = (flagName, trueValue = 'true') => {
  const value = getUrlParam(flagName);
  return value === trueValue;
};

/**
 * 構建URL字串
 * @param {string} basePath - 基礎路徑
 * @param {Object} params - 參數對象
 * @param {string} origin - 域名（預設為當前域名）
 * @returns {string} 完整的URL
 */
export const buildUrl = (basePath, params = {}, origin = window.location.origin) => {
  const url = new URL(basePath, origin);
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, value.toString());
    }
  });
  
  return url.toString();
};

/**
 * 構建相對路徑URL
 * @param {string} basePath - 基礎路徑  
 * @param {Object} params - 參數對象
 * @returns {string} 相對路徑URL
 */
export const buildRelativeUrl = (basePath, params = {}) => {
  const url = new URL(basePath, 'http://dummy.com');
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, value.toString());
    }
  });
  
  return url.pathname + url.search;
};

/**
 * 解析URL獲取所有參數
 * @param {string} url - URL字串（預設為當前頁面）
 * @returns {Object} 參數對象
 */
export const parseUrlParams = (url = window.location.search) => {
  const urlParams = new URLSearchParams(url);
  const params = {};
  
  urlParams.forEach((value, key) => {
    params[key] = value;
  });
  
  return params;
};

/**
 * 合併URL參數
 * @param {Object} newParams - 新參數對象
 * @param {boolean} keepExisting - 是否保留現有參數
 * @returns {Object} 合併後的參數對象
 */
export const mergeUrlParams = (newParams = {}, keepExisting = true) => {
  const existingParams = keepExisting ? parseUrlParams() : {};
  return { ...existingParams, ...newParams };
};

/**
 * 清空所有URL參數
 * @param {boolean} replace - 是否替換當前歷史記錄
 */
export const clearUrlParams = (replace = true) => {
  const url = new URL(window.location);
  url.search = '';
  
  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
};

/**
 * 檢查URL是否包含指定參數
 * @param {string} name - 參數名稱
 * @param {string} url - URL字串（預設為當前頁面）
 * @returns {boolean} 是否包含參數
 */
export const hasUrlParam = (name, url = window.location.search) => {
  const urlParams = new URLSearchParams(url);
  return urlParams.has(name);
};

/**
 * 獲取當前頁面的基礎URL（不含參數）
 * @returns {string} 基礎URL
 */
export const getBaseUrl = () => {
  const url = new URL(window.location);
  url.search = '';
  url.hash = '';
  return url.toString();
};

/**
 * 檢查是否為外部URL
 * @param {string} url - 要檢查的URL
 * @returns {boolean} 是否為外部URL
 */
export const isExternalUrl = (url) => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin !== window.location.origin;
  } catch (e) {
    return false;
  }
};

/**
 * 構建團隊公開頁面的分享連結
 * 自動帶上 ref 參數。defaultRef（成員自己的 inviteCode）優先於 URL 上的 ref。
 * @param {string} path - 路徑（如 '/peak-academy' 或 '/peak-academy/community/42'）
 * @param {Object} params - 額外 query 參數（如 { course: 1 }、{ member: 2 }）
 * @param {string|null} defaultRef - 成員的 invite code，優先於 URL ref
 * @returns {string} 完整的分享 URL
 */
export const buildTeamShareUrl = (path, params = {}, defaultRef = null) => {
  const refParam = defaultRef || new URLSearchParams(window.location.search).get('ref')
  const allParams = { ...params }
  if (refParam) allParams.ref = refParam
  return buildUrl(path, allParams)
}

/**
 * Deterministic short key for a teaching-showcase item, derived from its
 * identifying media URL (video_url wins over url). Used for showcase deep links
 * (`?showcase=<key>`) so a shared link survives item reordering without needing
 * stable IDs. The same function generates and resolves the key.
 * @param {{ video_url?: string, url?: string }} item
 * @returns {string} base36 hash (empty string if the item has no media URL)
 */
export const showcaseItemKey = (item) => {
  const seed = item?.video_url || item?.url || ''
  if (!seed) return ''
  let h = 5381
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) + h + seed.charCodeAt(i)) >>> 0
  }
  return h.toString(36)
}

/**
 * Resolve a showcase deep-link key to the index of the matching item.
 * Returns the first item whose showcaseItemKey equals `key`, or -1 when the key
 * is empty / no item matches (item deleted). Kept pure so the resolve behaviour
 * is unit-testable without a component harness.
 * @param {Array<{video_url?: string, url?: string}>} items
 * @param {string} key
 * @returns {number} matching index, or -1
 */
export const findShowcaseIndexByKey = (items, key) => {
  if (!key) return -1
  return (items || []).findIndex(item => showcaseItemKey(item) === key)
}

/**
 * 安全地開啟外部連結
 * @param {string} url - 外部連結
 * @param {boolean} newTab - 是否在新分頁開啟
 */
export const openExternalUrl = (url, newTab = true) => {
  if (!isExternalUrl(url)) {
    console.warn('openExternalUrl: URL appears to be internal');
  }
  
  if (newTab) {
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.noopener = true;
      newWindow.noreferrer = true;
    }
  } else {
    window.location.href = url;
  }
};